import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDevices } from '../store/devices';
import { useLiveEvents } from '../hooks/useLiveEvents';
import { api } from '../store/auth';
import toast from 'react-hot-toast';
import BackBtn from '../components/BackBtn';

export default function Processes() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { sendCommand } = useDevices();
  const [processes, setProcesses] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      const { data } = await api.get(`/devices/${deviceId}/processes/latest`);
      setProcesses(data.processes || []);
    } catch {
      // no cached data yet — wait for processes_ready event
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  // When agent responds, server signals us — fetch via REST (avoids large WS frame)
  useLiveEvents((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'processes_ready') {
      fetchLatest();
    }
  });

  const refresh = async () => {
    setLoading(true);
    try {
      await sendCommand(deviceId, 'processes', {});
      // response comes back via processes_ready event → fetchLatest()
    } catch {
      toast.error('Device offline');
      setLoading(false);
    }
  };

  // Auto-fetch on mount
  useEffect(() => { refresh(); }, [deviceId]);

  const kill = async (pid, name) => {
    if (!window.confirm(`Kill process "${name}" (PID ${pid})?`)) return;
    try {
      await sendCommand(deviceId, 'processes/kill', { pid });
      toast.success(`Kill signal sent to ${name}`);
      // Refresh after a moment
      setTimeout(refresh, 1500);
    } catch {
      toast.error('Failed to send kill command');
    }
  };

  const filtered = filter
    ? processes.filter(p =>
        p.name?.toLowerCase().includes(filter.toLowerCase()) ||
        p.username?.toLowerCase().includes(filter.toLowerCase()))
    : processes;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ fontWeight:700 }}>Process Manager</h2>
        <button onClick={refresh} disabled={loading} style={btnStyle}>
          {loading ? 'Waiting…' : '⟳ Refresh'}
        </button>
        <input value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filter by name or user…" style={inputStyle} />
        <span style={{ color:'var(--muted)', fontSize:12 }}>
          {filtered.length} / {processes.length} processes
        </span>
      </div>

      {processes.length === 0 ? (
        <div style={{ textAlign:'center', color:'var(--muted)', padding:60 }}>
          <p>{loading ? 'Fetching processes…' : 'No process data yet.'}</p>
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)', color:'var(--muted)',
                           textAlign:'left' }}>
                <Th>PID</Th>
                <Th>Name</Th>
                <Th>User</Th>
                <Th>Status</Th>
                <Th>CPU %</Th>
                <Th>RAM (MB)</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={i} style={{ borderBottom:'1px solid var(--border)',
                  background: i % 2 === 0 ? 'transparent' : 'var(--surface)20' }}>
                  <Td muted>{p.pid}</Td>
                  <Td style={{ fontFamily:'monospace', color:'var(--text)' }}>{p.name}</Td>
                  <Td muted>{p.username}</Td>
                  <Td style={{ color: p.status === 'running' ? 'var(--green)' : 'var(--muted)' }}>
                    {p.status}
                  </Td>
                  <Td>{p.cpu_pct?.toFixed(1)}%</Td>
                  <Td>{p.mem_mb}</Td>
                  <Td>
                    <button onClick={() => kill(p.pid, p.name)} style={{
                      padding:'2px 8px', background:'var(--red)15',
                      border:'1px solid var(--red)30', borderRadius:4,
                      color:'var(--red)', cursor:'pointer', fontSize:11,
                    }}>Kill</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th style={{ padding:'8px 12px', fontWeight:600, fontSize:11,
    textTransform:'uppercase', letterSpacing:.4 }}>{children}</th>;
}
function Td({ children, muted, style }) {
  return <td style={{ padding:'7px 12px', color: muted ? 'var(--muted)' : 'var(--text)',
    ...style }}>{children}</td>;
}

const btnStyle = {
  padding:'6px 14px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--muted)', cursor:'pointer', fontSize:12,
};
const inputStyle = {
  padding:'6px 10px', background:'var(--surface)', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--text)', fontSize:13, width:220,
};
